import { RouterProvider } from "react-router";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { createAppRouter } from "./routes";
import { setPendingRedirect, consumePendingRedirect } from "./utils/redirectState";
import { API_BASE } from "./utils/apiClient";
import { publicAnonKey } from "../../utils/supabase/info";
import { GlobalErrorBoundary } from "./components/shared/GlobalErrorBoundary";
import { RouteFallback } from "./components/shared/RouteFallback";

// Expose API_BASE on window for use by class components (SafeZone error boundary)
// that cannot use module imports in the same way as functional components.
(window as any).__ZAP_API_BASE = API_BASE;

// ── Data prefetch at module level ──
// Instead of a useless /health warm-up, prefetch the two most critical API
// endpoints (/templates and /users). This does DOUBLE DUTY:
//   1. Wakes the edge function from cold sleep (covers cold start)
//   2. Fetches actual data that the dashboard needs
// The promises are stored on window and consumed by templateApi.ts and
// userApi.ts, so the React effects don't fire duplicate requests.
const prefetchHeaders = {
  "Content-Type": "application/json",
  Authorization: `Bearer ${publicAnonKey}`,
};
const prefetchTemplates = fetch(`${API_BASE}/templates`, { headers: prefetchHeaders })
  .then(r => r.ok ? r.json() : null)
  .catch(() => null);
const prefetchUsers = fetch(`${API_BASE}/users`, { headers: prefetchHeaders })
  .then(r => r.ok ? r.json() : null)
  .catch(() => null);
(window as any).__ZAP_PREFETCH_TEMPLATES = prefetchTemplates;
(window as any).__ZAP_PREFETCH_USERS = prefetchUsers;
// Also serve as warm-up signal for AuthContext
(window as any).__ZAP_WARMUP = prefetchTemplates;

// Keep the edge function warm every 4 minutes so returning users never hit a cold start.
// Supabase edge functions sleep after ~5 min of inactivity — this prevents that.
setInterval(() => {
  fetch(`${API_BASE}/templates`, { headers: prefetchHeaders }).catch(() => {});
}, 4 * 60 * 1000);

/**
 * Query-param -> hash-route redirect system (multi-layer).
 *
 * Problem: Hash-based URLs (domain.com/#/client-board/xxx) get truncated by
 * WhatsApp, Telegram, and many email clients -- the # fragment is stripped.
 * Solution: share links use query parameters (?cb=xxx, ?t=xxx) which survive
 * all messaging platforms.
 *
 * We convert them to hash routes on page load, using THREE layers of defense:
 *
 *   Layer 1 (synchronous, pre-router):
 *     handleQueryParamRedirect() rewrites the URL via history.replaceState
 *     BEFORE createHashRouter() is called, so the router's initial location
 *     is already the correct hash route.
 *
 *   Layer 2 (synchronous, post-router):
 *     After creating the router, we check if router.state.location.pathname
 *     matches our expected redirect target. If not (Layer 1 failed for some
 *     reason), we call router.navigate() to force-navigate.
 *
 *   Layer 3 (React effect, in-tree fallback):
 *     RootLayout includes a QueryParamGuard that checks on mount whether
 *     query params are still present and navigates accordingly. This catches
 *     any remaining edge cases.
 *
 * Supported params:
 *   ?page=ROUTE       ->  /#/ROUTE  (universal, never stripped by messaging apps)
 *   ?cb=TEMPLATE_ID   ->  /#/client-board/TEMPLATE_ID
 *   ?t=TEMPLATE_ID    ->  /#/t/TEMPLATE_ID/
 *   ?intake=TEMPLATE_ID -> /#/intake/TEMPLATE_ID
 *
 * Also handles:
 *   - Path-based fallback: /intake-forms, /intake-login, /intake/xxx,
 *     /client-portal arriving as pathname (hash stripped by messaging apps)
 */

function handleQueryParamRedirect(): void {
  try {
    const raw = window.location.search;
    const hash = window.location.hash;
    const pathname = window.location.pathname;
    console.log("[Redirect L1] window.location =", {
      href: window.location.href,
      search: raw,
      hash,
      pathname,
    });

    const params = new URLSearchParams(raw);
    const cb = params.get("cb");
    const t = params.get("t");
    const intake = params.get("intake");
    const page = params.get("page");

    // ── ?page= universal redirect (messaging-app safe) ──
    // Examples:
    //   ?page=intake-forms        -> /#/intake-forms
    //   ?page=intake-login        -> /#/intake-login
    //   ?page=client-portal       -> /#/client-portal
    //   ?page=intake&id=TOKEN     -> /#/intake/TOKEN
    if (page) {
      const id = params.get("id");
      let targetPath: string;
      if (page === "intake" && id) {
        targetPath = "/intake/" + encodeURIComponent(id);
      } else {
        // Use page value directly as the route path (e.g. "intake-forms" -> "/intake-forms")
        targetPath = "/" + page.replace(/^\/+/, "");
      }
      console.log("[Redirect L1] ?page= redirect:", page, "-> hash", targetPath);
      setPendingRedirect(targetPath);
      window.history.replaceState(null, "", "/" + "#" + targetPath);
      return;
    }

    if (intake) {
      const targetPath = "/intake/" + encodeURIComponent(intake);
      setPendingRedirect(targetPath);
      const newUrl = window.location.pathname + "#" + targetPath;
      console.log("[Redirect L1] Rewriting to:", newUrl);
      window.history.replaceState(null, "", newUrl);
      console.log("[Redirect L1] After replaceState, hash =", window.location.hash);
      return;
    }

    if (cb) {
      const targetPath = "/client-board/" + encodeURIComponent(cb);
      setPendingRedirect(targetPath);
      const newUrl = window.location.pathname + "#" + targetPath;
      console.log("[Redirect L1] Rewriting to:", newUrl);
      window.history.replaceState(null, "", newUrl);
      console.log("[Redirect L1] After replaceState, hash =", window.location.hash);
      return;
    }

    if (t) {
      const targetPath = "/t/" + encodeURIComponent(t) + "/";
      setPendingRedirect(targetPath);
      const newUrl = window.location.pathname + "#" + targetPath;
      console.log("[Redirect L1] Rewriting to:", newUrl);
      window.history.replaceState(null, "", newUrl);
      console.log("[Redirect L1] After replaceState, hash =", window.location.hash);
      return;
    }

    console.log("[Redirect L1] No redirect params found.");

    // ── Path-based fallback ──
    // If the hash was stripped by a messaging app, the intended hash route
    // may appear as the pathname instead (e.g. /intake-forms instead of /#/intake-forms).
    // Detect this and rewrite to the correct hash route.
    const basePath = window.location.pathname.replace(/\/+$/, "");

    // Match known standalone routes that live outside the main layout
    const PATH_TO_HASH: Record<string, string> = {
      "/intake-forms": "/intake-forms",
      "/intake-login": "/intake-login",
      "/client-portal": "/client-portal",
    };

    // Check exact matches first
    if (PATH_TO_HASH[basePath]) {
      const targetPath = PATH_TO_HASH[basePath];
      console.log("[Redirect L1] Path-based fallback: pathname", basePath, "-> hash", targetPath);
      setPendingRedirect(targetPath);
      window.history.replaceState(null, "", "/" + "#" + targetPath);
      return;
    }

    // Check /intake/TOKEN pattern (dynamic segment)
    const intakeMatch = basePath.match(/^\/intake\/(.+)$/);
    if (intakeMatch) {
      const targetPath = "/intake/" + intakeMatch[1];
      console.log("[Redirect L1] Path-based fallback: pathname", basePath, "-> hash", targetPath);
      setPendingRedirect(targetPath);
      window.history.replaceState(null, "", "/" + "#" + targetPath);
      return;
    }
  } catch (err) {
    console.error("[Redirect L1] Error:", err);
  }
}

// -- Layer 1: Rewrite query params -> hash route --
handleQueryParamRedirect();

// -- Layer 2: Create router, then verify it landed at the right place --
const router = createAppRouter();

const pendingPath = consumePendingRedirect();
if (pendingPath) {
  const actual = router.state.location.pathname;
  console.log("[Redirect L2] Expected:", pendingPath, "| Actual:", actual);

  if (actual !== pendingPath) {
    console.warn("[Redirect L2] Mismatch! Forcing router.navigate to:", pendingPath);
    router.navigate(pendingPath, { replace: true });
  } else {
    console.log("[Redirect L2] Router location matches -- no correction needed.");
  }
}

/** Root component -- renders the hash router */
export default function App() {
  return (
    <GlobalErrorBoundary>
      <RouterProvider router={router} fallbackElement={<RouteFallback />} />
      <SpeedInsights />
    </GlobalErrorBoundary>
  );
}